import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import {
  Loader2, Eye, Lock, Unlock, ExternalLink, BookOpen, GalleryHorizontal,
  BookLock, Store, CreditCard, Feather, ShieldAlert,
} from "lucide-react";

// Every public-facing segment of the site, grouped by the journey a person walks
// through. Each view opens in a new tab. "New visitor" uses ?fresh=1 to wipe any
// remembered access; "With full access" appends the admin preview code.
const GROUPS = [
  {
    key: "book",
    icon: BookOpen,
    title: "The Book — the reader journey",
    blurb: "Exactly what a reader sees, from the cover to the paywall to the full volume.",
    items: [
      { id: "book-entry", label: "Home & Chapter One", desc: "The cover, the entry veil, and the free Chapter One.", povs: ["new", "owner"] },
      { id: "book-paywall", label: "The paywall (end of Chapter One)", desc: "\"The Threshold of the Exhale\" — purchase + bundle + access-code.", povs: ["paywall"] },
      { id: "book-full", label: "The full book", desc: "All chapters unlocked, as a paying reader sees them.", povs: ["owner"] },
    ],
  },
  {
    key: "gallery",
    icon: GalleryHorizontal,
    title: "The Art Gallery",
    blurb: "The dual-gated exhibition — locked teaser vs. the complete collection.",
    items: [
      { id: "gallery-locked", label: "Gallery teaser (locked)", desc: "What a non-member sees: the gate + a glimpse.", path: "/vault/gallery", povs: ["new"] },
      { id: "gallery-full", label: "Full gallery (with access)", desc: "The complete curated exhibition + lightbox + keepsake prints.", path: "/vault/gallery", povs: ["owner"] },
      { id: "gallery-preview", label: "Standalone gallery preview", desc: "The /gallery/preview marketing page.", path: "/gallery/preview", povs: ["plain"] },
    ],
  },
  {
    key: "vault",
    icon: BookLock,
    title: "The Language Vault",
    blurb: "The members' sanctuary — the gate vs. the interior.",
    items: [
      { id: "vault-locked", label: "Vault gate (locked)", desc: "The poetic intro + unlock gate for non-members.", path: "/vault", povs: ["new"] },
      { id: "vault-interior", label: "Vault interior (member)", desc: "Reframes, audio reflections, keepsake downloads.", path: "/vault", povs: ["owner"] },
    ],
  },
  {
    key: "offerings",
    icon: Store,
    title: "Offerings & Doorways",
    blurb: "The support pathways and their individual doorway pages.",
    items: [
      { id: "off-hub", label: "Offerings hub", desc: "The four doorways overview.", path: "/offerings", povs: ["plain"] },
      { id: "off-orientation", label: "Orientation doorway", desc: "The Biopsychosocial Orientation detail page.", path: "/offerings/biopsychosocial-orientation", povs: ["plain"] },
      { id: "off-immersion", label: "Immersion doorway", desc: "The 8-Month Therapeutic Immersion detail page.", path: "/offerings/therapeutic-immersion", povs: ["plain"] },
      { id: "off-getbook", label: "Get the book (offers)", desc: "The standalone offers landing.", path: "/get-the-book", povs: ["plain"] },
    ],
  },
  {
    key: "checkout",
    icon: CreditCard,
    title: "Checkout & Payment",
    blurb: "The purchase flow and its outcomes.",
    items: [
      { id: "co-book", label: "Checkout — the book", desc: "Secure Stripe checkout for the $33.99 book.", path: "/checkout?product=braced-no-more-book", povs: ["plain"] },
      { id: "co-bundle", label: "Checkout — book + Vault bundle", desc: "The Integrated Sanctuary order-bump pre-checked.", path: "/checkout?product=braced-no-more-book&bump=1", povs: ["plain"] },
      { id: "co-success", label: "Payment success", desc: "The post-purchase confirmation screen.", path: "/payment/success", povs: ["plain"] },
      { id: "co-cancel", label: "Payment cancelled", desc: "The gentle cancel screen.", path: "/payment/cancel", povs: ["plain"] },
    ],
  },
  {
    key: "misc",
    icon: Feather,
    title: "Reflection & Legal",
    blurb: "Supporting pages.",
    items: [
      { id: "reflect", label: "Reflect / journal", desc: "The reader reflection space.", path: "/reflect", povs: ["plain"] },
      { id: "privacy", label: "Privacy policy", desc: "The privacy page.", path: "/privacy", povs: ["plain"] },
    ],
  },
];

export default function AdminPreview() {
  const [token] = useState(() => getAdminToken());
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/preview-code`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setCode(data.access_code || ""))
      .catch((e) => { if (e.response?.status === 401) { adminLogout(); return; } setErr("Could not prepare a preview code."); })
      .finally(() => setLoading(false));
  }, [token]);

  const urlFor = (item, pov) => {
    const base = item.path || "/";
    const join = (q) => base + (base.includes("?") ? "&" : "?") + q;
    if (pov === "new") return join("fresh=1");
    if (pov === "paywall") return join("fresh=1&paywall=1");
    if (pov === "owner") return code ? join(`code=${encodeURIComponent(code)}`) : base;
    return base; // plain
  };

  const open = (item, pov) => window.open(urlFor(item, pov), "_blank", "noopener");

  const POV_META = {
    new: { label: "New visitor", icon: Lock, cls: "bg-ink text-ivory-50 hover:bg-ink/90" },
    paywall: { label: "See the paywall", icon: ShieldAlert, cls: "bg-terracotta/90 text-ivory-50 hover:bg-terracotta" },
    owner: { label: "With full access", icon: Unlock, cls: "bg-sage/30 text-ink ring-1 ring-sage/50 hover:bg-sage/40" },
    plain: { label: "Open page", icon: ExternalLink, cls: "bg-ivory-50 text-ink ring-1 ring-ivory-400/70 hover:ring-gold/50" },
  };

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-preview">
      <div className="mx-auto max-w-5xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-gold/15 text-gold-deep"><Eye className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-3xl text-ink">Preview the whole site</h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-soft">
              Walk every segment exactly as a reader or viewer experiences it. Each opens in a new tab.
              <span className="font-medium text-ink"> New visitor</span> shows the free / locked state,
              <span className="font-medium text-ink"> With full access</span> shows the unlocked view, and
              <span className="font-medium text-ink"> See the paywall</span> jumps straight to the Chapter One gate.
            </p>
          </div>
        </div>

        {loading && <div className="mt-8 flex items-center gap-2 text-ink-soft" data-testid="preview-loading"><Loader2 className="h-4 w-4 animate-spin" /> Preparing a preview pass…</div>}
        {err && <p className="mt-6 text-sm text-terracotta" data-testid="preview-error">{err}</p>}

        {!loading && (
          <div className="mt-8 space-y-7 pb-16">
            {GROUPS.map((g) => {
              const GIcon = g.icon;
              return (
                <section key={g.key} data-testid={`preview-group-${g.key}`} className="rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/60 sm:p-6">
                  <div className="flex items-center gap-2.5">
                    <GIcon className="h-5 w-5 text-ink-soft" />
                    <h2 className="font-display text-lg text-ink">{g.title}</h2>
                  </div>
                  <p className="mt-1 text-sm text-ink-soft">{g.blurb}</p>
                  <div className="mt-4 divide-y divide-ivory-400/50">
                    {g.items.map((item) => (
                      <div key={item.id} data-testid={`preview-item-${item.id}`} className="flex flex-col gap-3 py-3.5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="font-display text-ink">{item.label}</p>
                          <p className="text-xs text-ink-muted">{item.desc}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 sm:flex-none">
                          {item.povs.map((pov) => {
                            const m = POV_META[pov];
                            const PIcon = m.icon;
                            return (
                              <button key={pov} type="button" onClick={() => open(item, pov)}
                                data-testid={`preview-${item.id}-${pov}`}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 font-display text-xs transition ${m.cls}`}>
                                <PIcon className="h-3.5 w-3.5" /> {m.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
